import React, { Suspense, lazy, useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  GraduationCap, Inbox, AlarmClock, Users, Clock, Siren, UserCog, Loader2, ShieldCheck, Grid3X3, CheckCircle2, BadgeCheck, Activity, DollarSign,
  Search, History, Building2, Eye, BookOpen, Trophy, Megaphone, ShieldAlert, MessageSquare, UserMinus, Archive,
} from 'lucide-react';
import { ExpiringTab, WorkerLookupTab, AuditTab } from './AdminHrisConsole';
import HRISDocumentReview from './HRISDocumentReview';
import AccessAlertsPanel from './AccessAlertsPanel';
import EmailVerifyNudge from './EmailVerifyNudge';
import WorkerInbox from '../WorkerInbox';
import useUnreadMessages from '../../hooks/useUnreadMessages';

const HRISTraining = lazy(() => import('./HRISTraining'));
const HRISUsersPanel = lazy(() => import('./HRISUsersPanel'));
const HRISResultsMatrix = lazy(() => import('./HRISResultsMatrix'));
const HRISWorkerVerification = lazy(() => import('./HRISWorkerVerification'));
const HRISComplianceOverview = lazy(() => import('./HRISComplianceOverview'));
const HRISDigestCard = lazy(() => import('./HRISDigestCard'));
const PayAnalytics = lazy(() => import('../agent/AgencyPayAnalytics'));
const EmployerWorkerDirectory = lazy(() => import('../employer/EmployerWorkerDirectory'));
const TimesheetsPanel = lazy(() => import('../employer/TimesheetsPanel'));
const HRISWorkerTraining = lazy(() => import('./HRISWorkerTraining'));
const HRISLeaderboard = lazy(() => import('./HRISLeaderboard'));
const CommunityBoard = lazy(() => import('../community/CommunityBoard'));
const OffboardingPanel = lazy(() => import('../org/OffboardingPanel'));
const FormerStaffPanel = lazy(() => import('../org/FormerStaffPanel'));

const Spinner = () => <div className="text-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
const SCOPE_KEY = 'hris_admin_org_scope';

/**
 * OrgHrisConsole — the organisation's HR Information System. Embedded in the Employer and
 * Agency portals (HR tab), rendered full-page for HR sub-users at /hr, and — with `adminMode` —
 * used unchanged by the platform Admin dashboard (platform-wide, or read-only "view as org").
 */
export default function OrgHrisConsole({ initialTab = 'overview', adminMode = false }) {
  const [tab, setTab] = useState(initialTab);
  const { unread, refresh: refreshUnread } = useUnreadMessages(adminMode ? 0 : 20000);
  const [ctx, setCtx] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [scope, setScope] = useState(() => (adminMode && typeof window !== 'undefined' ? sessionStorage.getItem(SCOPE_KEY) || '' : ''));

  useEffect(() => {
    if (!adminMode) return undefined;
    if (scope) sessionStorage.setItem(SCOPE_KEY, scope); else sessionStorage.removeItem(SCOPE_KEY);
    return () => sessionStorage.removeItem(SCOPE_KEY);
  }, [adminMode, scope]);
  useEffect(() => { if (adminMode) apiCall('/api/admin/orgs').then(r => setOrgs(r.orgs || [])).catch(() => {}); }, [adminMode]);
  useEffect(() => { setCtx(null); apiCall('/api/hris/org-context').then(setCtx).catch(() => setCtx({})); }, [scope]);

  const platformWide = adminMode && !scope;           // admin, no org selected → cross-org view
  const scopedView = adminMode && !!scope;             // admin viewing one org read-only
  const areas = ctx?.areas || null;
  const can = (area) => !areas || areas.includes(area);
  const orgOnly = (id) => platformWide ? { ...id, orgOnly: true } : id;
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    orgOnly({ id: 'community', label: 'Community', icon: Megaphone }),
    orgOnly({ id: 'messages', label: 'Messages', icon: MessageSquare }),
    ...(can('training') ? [{ id: 'training', label: 'Training Studio', icon: GraduationCap }, { id: 'results', label: 'Training Results', icon: Grid3X3 }, { id: 'leaderboard', label: 'Leaderboard', icon: Trophy }] : []),
    ...(can('verification') ? [{ id: 'verification', label: 'Worker Verification', icon: BadgeCheck }] : []),
    ...(can('documents') ? [{ id: 'queue', label: 'Document Review', icon: Inbox }, { id: 'expiring', label: 'Expiring Credentials', icon: AlarmClock }, { id: 'access', label: 'Access Alerts', icon: ShieldAlert }] : []),
    ...(can('workers') ? [{ id: 'workers', label: 'Worker Directory', icon: Users }, orgOnly({ id: 'offboarding', label: 'Offboarding', icon: UserMinus }), orgOnly({ id: 'former', label: 'Former Staff', icon: Archive })] : []),
    ...(can('timesheets') ? [orgOnly({ id: 'timesheets', label: 'Timesheets', icon: Clock })] : []),
    ...(can('incidents') ? [{ id: 'incidents', label: 'Incident Reports', icon: Siren }] : []),
    ...(can('pay') ? [orgOnly({ id: 'pay', label: 'Pay Analytics', icon: DollarSign })] : []),
    ...(ctx?.is_org_admin || scopedView ? [{ id: 'hr-users', label: 'HR Users', icon: UserCog }] : []),
    ...(adminMode ? [{ id: 'lookup', label: 'Worker Lookup', icon: Search }, { id: 'audit', label: 'Audit Log', icon: History }] : [{ id: 'my-training', label: 'My Training', icon: BookOpen }]),
  ];
  const activeTab = tabs.find(t => t.id === tab);
  const scopedOrg = orgs.find(o => o._id === scope);

  return (
    <div className="space-y-4" data-testid="org-hris-console">
      <div className="rounded-3xl bg-slate-900 text-white p-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(ellipse_at_top_right,_#4f46e5_0%,_transparent_55%)]" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-indigo-200 mb-1"><ShieldCheck className="w-4 h-4" /> HR Information System</div>
            <h2 className="text-2xl font-bold" data-testid="hris-heading">
              {platformWide ? 'Platform · HRIS' : `${ctx?.org_name || 'Organisation'} · HRIS`}
            </h2>
            <p className="text-sm text-white/70 mt-1 max-w-2xl">
              {platformWide
                ? 'Every organisation\'s training, compliance documents, verification and incidents in one place. Pick an organisation to view its HRIS exactly as its HR team sees it.'
                : 'Training, compliance documents, workforce records, timesheets and incident reports — one system for your HR team.'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {ctx?.is_hr_user && (
              <span className="text-xs bg-white/10 border border-white/20 rounded-full px-3 py-1" data-testid="hris-acting-as">Signed in as HR · {ctx.hr_name}</span>
            )}
            {adminMode && (
              <label className="flex items-center gap-2 text-xs bg-white/10 border border-white/20 rounded-xl px-3 py-2" data-testid="hris-org-scope">
                <Building2 className="w-4 h-4 text-indigo-200" />
                <select value={scope} onChange={e => { setScope(e.target.value); setTab('overview'); }}
                  className="bg-transparent text-white text-sm font-semibold focus:outline-none [&>option]:text-slate-900" data-testid="hris-org-scope-select">
                  <option value="">All organisations (platform-wide)</option>
                  {orgs.map(o => <option key={o._id} value={o._id}>{o.company_name || o.full_name} · {o.role === 'agent' ? 'Agency' : 'Employer'}</option>)}
                </select>
              </label>
            )}
            {scopedView && (
              <span className="inline-flex items-center gap-1 text-[11px] bg-amber-400/20 border border-amber-300/40 text-amber-100 rounded-full px-3 py-1" data-testid="hris-readonly-badge">
                <Eye className="w-3 h-3" /> Viewing {scopedOrg?.company_name || scopedOrg?.full_name || 'organisation'} · read-only — use Support Mode to make changes
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'} ${t.orgOnly ? 'opacity-60' : ''}`}
              data-testid={`hris-tab-${t.id}`}>
              <Icon className="w-4 h-4" /> {t.label}
              {t.id === 'messages' && unread > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid="hr-messages-unread-badge">{unread > 99 ? '99+' : unread}</span>
              )}
            </button>
          );
        })}
      </div>

      {ctx === null ? <Spinner /> : (
        <Suspense fallback={<Spinner />}>
          <div key={scope}>
            {activeTab?.orgOnly ? <PickOrgNotice label={activeTab.label} /> : (<>
              {tab === 'overview' && (<>
                {!platformWide && !scopedView && <EmailVerifyNudge email={ctx?.my_email} verified={ctx?.my_email_verified !== false} onVerified={() => apiCall('/api/hris/org-context').then(setCtx).catch(() => {})} />}
                {!platformWide && <HRISDigestCard isOrgAdmin={!!ctx?.is_org_admin} />}
                <HRISComplianceOverview onNavigate={(id) => setTab(tabs.some(t => t.id === id) ? id : 'overview')} />
              </>)}
              {tab === 'community' && <CommunityBoard />}
              {tab === 'messages' && <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="hr-inbox" onClick={() => setTimeout(refreshUnread, 1500)}><WorkerInbox currentUserId={ctx?.org_id} /></div>}
              {tab === 'training' && <HRISTraining />}
              {tab === 'results' && <HRISResultsMatrix />}
              {tab === 'leaderboard' && <HRISLeaderboard />}
              {tab === 'verification' && <HRISWorkerVerification hideClaim={adminMode} />}
              {tab === 'queue' && <HRISDocumentReview />}
              {tab === 'expiring' && <ExpiringTab />}
              {tab === 'access' && <AccessAlertsPanel />}
              {tab === 'workers' && <EmployerWorkerDirectory />}
              {tab === 'offboarding' && <OffboardingPanel />}
              {tab === 'former' && <FormerStaffPanel />}
              {tab === 'timesheets' && <TimesheetsPanel />}
              {tab === 'incidents' && <IncidentsTab />}
              {tab === 'pay' && <PayAnalytics />}
              {tab === 'hr-users' && (ctx?.is_org_admin || scopedView) && <HRISUsersPanel />}
              {tab === 'my-training' && <HRISWorkerTraining />}
              {tab === 'lookup' && <WorkerLookupTab />}
              {tab === 'audit' && <AuditTab />}
            </>)}
          </div>
        </Suspense>
      )}
    </div>
  );
}

function PickOrgNotice({ label }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-2xl py-14 text-center" data-testid="hris-pick-org-notice">
      <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
      <p className="text-slate-700 font-semibold text-sm">{label} is organisation-specific</p>
      <p className="text-slate-400 text-xs mt-1">Choose an organisation from the selector above to view its {label.toLowerCase()}.</p>
    </div>
  );
}

function IncidentsTab() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('active');
  const load = () => apiCall(`/api/hris/incidents${filter === 'all' ? '' : `?status=${filter}`}`).then(r => setRows(r.incidents || [])).catch(e => toast.error(e.message));
  useEffect(() => { load(); }, [filter]);  

  const resolve = async (r) => {
    const note = window.prompt('Resolution note (optional)') ?? '';
    try {
      await apiCall(`/api/hris/incidents/${r._id}/resolve`, { method: 'POST', body: JSON.stringify({ note }) });
      toast.success('Incident marked resolved'); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid="hris-incidents">
      <div className="flex gap-2">
        {['active', 'resolved', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${filter === f ? 'bg-red-600 text-white border-red-600' : 'bg-white text-slate-700 border-slate-200'}`} data-testid={`incidents-filter-${f}`}>
            {f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl py-12 text-center text-slate-400 text-sm">No {filter === 'all' ? '' : filter} incident reports.</div>
      ) : rows.map(r => (
        <div key={r._id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid={`incident-row-${r._id}`}>
          <div className={`p-2 rounded-lg ${r.status === 'resolved' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}><Siren className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-900">{r.worker_name || 'Worker'}</p>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.type}</span>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{r.status}</span>
            </div>
            <p className="text-sm text-slate-700 mt-1">{r.message}</p>
            <p className="text-[11px] text-slate-400 mt-1">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}{r.resolved_at ? ` · resolved ${new Date(r.resolved_at).toLocaleString()} by ${r.resolved_by_name || 'HR'}` : ''}{r.resolution_note ? ` — ${r.resolution_note}` : ''}</p>
          </div>
          {r.status !== 'resolved' && (
            <button onClick={() => resolve(r)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`incident-resolve-${r._id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
